import { faFilter, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import {
  ButtonGroup,
  Col,
  Container,
  Row,
  ToggleButton,
} from "react-bootstrap";
import { selectFilter, textFilter } from "react-bootstrap-table2-filter";
import Button from "react-bootstrap/Button";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import DeleteDialog from "../../components/dialogs/deleteDialog/DeleteDialog";
import UnsavedChangesDialog from "../../components/dialogs/unsavedChangesDialog/UnsavedChangesDialog";
import PaginationTable from "../../components/paginationTable/PaginationTable";
import APIErrorHelper from "../../helpers/APIErrorHelper";
import CamplaCoreService from "../../services/CamplaCoreService";
import StorageService from "../../services/StorageService";
import CreateUserDialog from "./dialogs/createUserDialog/CreateUserDialog";
import UsersActionCell from "./usersActionCell/UsersActionCell";
import UpdateUserDialog from "../users/dialogs/updateUserDialog/UpdateUserDialog";

class Users extends React.Component {
  tableId = "usersTable";
  constructor(props) {
    super(props);
    this.roles = ["Lecturer", "Administrator"];
    let showFilters = StorageService.getTableConfigValue(
      this.tableId,
      "showFilters"
    );
    this.state = {
      users: [],
      showCreateModal: false,
      showDeleteModal: false,
      showUpdateUserModal: false,
      showUnsavedChangesDialog: false,
      closeAction: () => {},
      userToDeleteOrEdit: null,
      errorMessage: "",
      displayFilter: showFilters ? "initial" : "none",
      showFilters,
      dialogApiError: "",
      apiError: "",
      isUpdating: false,
    };

    this.loadData = this.loadData.bind(this);
    this.loadCachedData = this.loadCachedData.bind(this);
    this.handleCreateUser = this.handleCreateUser.bind(this);
    this.handleDeleteUser = this.handleDeleteUser.bind(this);
    this.hideCreationDialog = this.hideCreationDialog.bind(this);
    this.onHideCreationDialog = this.onHideCreationDialog.bind(this);
    this.getUserRoleValue = this.getUserRoleValue.bind(this);
    this.handleUpdateUser = this.handleUpdateUser.bind(this);
  }

  async handleUpdateUser(user) {

    try {
      this.setState({ isUpdating: true });

      const updatedUser = await CamplaCoreService.updateUser(
          this.state.userToDeleteOrEdit._id,
          user
      );

      let index = this.state.users
        .map((c) => c._id)
        .indexOf(this.state.userToDeleteOrEdit._id);
      if (index !== -1) {
        this.state.users[index] = updatedUser;
      }

      if (typeof updatedUser["mfaKeyUri"] !== "undefined"){
        this.setState({
          users: Object.assign([], this.state.users),
          isUpdating: false,
        });
      }else{
        this.setState({
          showUpdateUserModal: false,
          users: Object.assign([], this.state.users),
          userToDeleteOrEdit: null,
          isUpdating: false,
        });
      }
      return updatedUser;
    } catch (err) {
      this.setState({
        isUpdating: false,
        dialogApiError: APIErrorHelper.getError(
          err,
          () => {
            this.setState({ dialogApiError: "" });
          },
          {
            PRECONDITION_FAILED: "Classes.APIError.PreconditionFailed",
            NOT_FOUND: "Classes.APIError.NotFound",
            CONFLICT: "Classes.APIError.DeleteConflict",
          },
          this.props.t
        ),
      });
    }
    return null;
  }

  async componentDidMount() {
    this.paginationTableRef = React.createRef();
    this.loadCachedData();
    this.setState(await this.loadData());
  }

  async handleCreateUser(user) {
    try {
      const createdUser = await CamplaCoreService.createUser(user);
      const updatedUsers = [...this.state.users, createdUser];
      StorageService.set("users", updatedUsers);
      if (typeof createdUser["mfaKeyUri"] !== "undefined"){
        this.setState({
          users: updatedUsers,
        });
      }else {
        this.setState({
          showCreateModal: false,
          users: updatedUsers,
        });
      }
      return createdUser;
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            NOT_FOUND: "Users.APIError.NotFound",
            PRECONDITION_FAILED: "Users.APIError.PreconditionFailed",
            CONFLICT: "Users.APIError.CreateConflict",
          },
          this.props.t
        ),
      });
    }
    return null;
  }

  async handleDeleteUser() {
    try {
      await CamplaCoreService.deleteUser(this.state.userToDeleteOrEdit._id);
      const updatedUsers = this.state.users.filter(
        (x) => x._id !== this.state.userToDeleteOrEdit._id
      );
      StorageService.set("users", updatedUsers);
      this.setState({
        showDeleteModal: false,
        users: updatedUsers,
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED: "Users.APIError.PreconditionFailed",
            NOT_FOUND: "Users.APIError.NotFound",
            CONFLICT: "Users.APIError.DeleteConflict",
          },
          this.props.t
        ),
      });
    }
  }

  loadCachedData() {
    const users = StorageService.getReferenceType("users");
    if (users) {
      this.setState({
        users,
      });
    }
  }

  async loadData() {
    try {
      const users = await CamplaCoreService.getAllUsers();
      StorageService.set("users", users);
      this.setState({ users });
      return {
        users,
      };
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        ),
      });
    }
    return {
      users: [],
    };
  }

  hideCreationDialog() {
    this.setState({ showCreateModal: false, dialogApiError: "" });
  }

  onHideCreationDialog(isFormDirty) {
    if (isFormDirty) {
      this.setState({
        showUnsavedChangesDialog: true,
        closeAction: this.hideCreationDialog,
      });
    } else {
      this.hideCreationDialog();
    }
  }

  render() {
    const { t } = this.props;
    const columns = [
      {
        dataField: "email",
        text: t("Users.ColumnEmail"),
        sort: true,
        filter: textFilter({
          style: { display: this.state.displayFilter },
          placeholder: t("Users.FilterEmailPlaceholder"),
        }),
        formatter: (email, user) => (
          <Link
            className="item-link"
            to={{
              pathname: `/users/${user._id}`,
              state: {
                user,
              },
            }}
          >
            {email}
          </Link>
        ),
      },
      {
        dataField: "role",
        text: t("Users.ColumnRole"),
        formatter: this.getUserRoleValue,
        sort: true,
        filter: selectFilter({
          options: [
            { label: this.getUserRoleValue(0), value: 0 },
            { label: this.getUserRoleValue(1), value: 1 },
          ],
          style: { display: this.state.displayFilter },
          placeholder: t("Users.FilterRolePlaceholder"),
        }),
      },
      {
        dataField: "actions",
        text: "",
        headerFormatter: () => (
          <ButtonGroup toggle style={{ float: "right", marginRight: "-12px" }}>
            <ToggleButton
              type="checkbox"
              value={1}
              className="btn-green btn-sm"
              checked={this.state.showFilters}
              onChange={(e) => {
                this.setState({
                  showFilters: e.currentTarget.checked,
                  displayFilter: e.currentTarget.checked ? "initial" : "none",
                });
                StorageService.setTableConfigValue(
                  this.tableId,
                  "showFilters",
                  e.currentTarget.checked
                );
              }}
            >
              <FontAwesomeIcon icon={faFilter} />
            </ToggleButton>
          </ButtonGroup>
        ),
        headerClasses: "actionHeader",
        isDummyField: true,
        csvExport: false,
        formatter: (cell, user) => (
          <UsersActionCell
            onDelete={() =>
              this.setState({
                showDeleteModal: true,
                userToDeleteOrEdit: Object.assign({}, user),
              })
            }
            onEdit={() => {
              this.setState({
                showUpdateUserModal: true,
                userToDeleteOrEdit: user,
              });
            }}
          />
        ),
      },
    ];

    return (
      <div>
        <UnsavedChangesDialog
          show={this.state.showUnsavedChangesDialog}
          onHide={() => this.setState({ showUnsavedChangesDialog: false })}
          onSubmit={() => {
            this.setState({ showUnsavedChangesDialog: false });
            this.state.closeAction();
          }}
        />
        <DeleteDialog
          show={this.state.showDeleteModal}
          onHide={() =>
            this.setState({ showDeleteModal: false, dialogApiError: "" })
          }
          onSubmit={this.handleDeleteUser}
          dialogApiError={this.state.dialogApiError}
          title={t("Users.DeleteModal.Title")}
          body={t("Users.DeleteModal.Text", {
            email: this.state.userToDeleteOrEdit?.email,
          })}
          rightButtonText={t("Users.DeleteModal.Confirm")}
          leftButtonText={t("DialogCancel")}
        />
        <CreateUserDialog
          show={this.state.showCreateModal}
          onHide={this.onHideCreationDialog}
          onSubmit={this.handleCreateUser}
          dialogApiError={this.state.dialogApiError}
        />

        <UpdateUserDialog
          userToDeleteOrEdit={this.state.userToDeleteOrEdit}
          //users={this.state.users}
          isUpdating={this.state.isUpdating}
          show={this.state.showUpdateUserModal}
          dialogApiError={this.state.dialogApiError}
          onHide={(isFormDirty) => {
            if (isFormDirty) {
              this.setState({
                dialogApiError: null,
                showUnsavedChangesDialog: true,
                closeAction: () =>
                  this.setState({
                    showUpdateUserModal: false,
                  }),
              });
            } else {
              this.setState({
                dialogApiError: null,
                showUpdateUserModal: false,
              });
            }
          }}
          onSubmit={this.handleUpdateUser}
          user={this.state.userToDeleteOrEdit}
        />

        <Container className="mt-3">
          <Row className="pageTitle">
            <Col>
              <h2>{t("Users.Title")}</h2>
            </Col>
            <Col>
              <Button
                className="btn-green"
                style={{ float: "right" }}
                onClick={() => this.setState({ showCreateModal: true })}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Col>
          </Row>
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Row>
            <Col>
              <PaginationTable
                data={this.state.users}
                sizePerPage={10}
                paginationSize={3}
                columns={columns}
                keyField="_id"
                ref={this.paginationTableRef}
                id={this.tableId}
                view="users"
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  getUserRoleValue(role) {
    return this.roles[role];
  }
}

export default withTranslation()(Users);
